import React from "react";
import moment from "moment";
import { IconArrowRight, IconInfoCircle, IconRobot } from "@tabler/icons-react";

/*
 * Presentational pieces of the Change History view. They only render what the
 * API returns (see backend change_logs): values arrive formatted, null = "-".
 */

const EMPTY = "-";

const initials = (name) => {
  const parts = String(name || "")
    .split(/[\s._-]+/)
    .filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};

const ACTION_BADGE = {
  CREATED: { className: "ch-badge-created", label: "Created" },
  MODIFIED: { className: "ch-badge-modified", label: "Modified" },
  DELETED: { className: "ch-badge-deleted", label: "Deleted" },
};

/* What the grouped rows inside one card are called. */
const GROUP_NOUN = {
  products_sales: ["product", "products"],
  products_purchasing: ["product", "products"],
  variants: ["variant", "variants"],
  kit_products: ["kit product", "kit products"],
  contract_vehicles: ["contract vehicle", "contract vehicles"],
};

/* Product lines say which tab they came from. */
const KIND_BADGE = {
  products_sales: { className: "ch-badge-sales", label: "Sales" },
  products_purchasing: { className: "ch-badge-purchasing", label: "Purchase" },
};

const TRACK_LABEL = {
  finance: "Finance",
  logistics: "Logistics",
  stage: "Stage",
};

export const ActorAvatar = ({ card }) =>
  card.actor_type === "AUTOMATION" ? (
    <span className="ch-avatar ch-avatar-automation" title="Automation">
      <IconRobot size={16} stroke={1.5} />
    </span>
  ) : (
    <span className="ch-avatar" title={card.actor_name || "Unknown user"}>
      {initials(card.actor_name)}
    </span>
  );

const CardHeader = ({ card, showSection, plain, children }) => (
  <div className={`ch-card-head${plain ? " ch-card-head-plain" : ""}`}>
    <div className="ch-actor">
      <ActorAvatar card={card} />
      <div className="text-truncate">
        <div className="ch-actor-name text-truncate">
          {card.actor_name || "Unknown user"}
        </div>
        <div className="ch-actor-meta">
          {moment(card.created_at).format("MM/DD/YYYY HH:mm")}
          {showSection && card.section_label ? ` · in ${card.section_label}` : ""}
        </div>
      </div>
    </div>
    <div className="ch-head-right">{children}</div>
  </div>
);

const ChangeRequestFooter = ({ card }) => {
  if (!card.change_request_id) {
    return null;
  }
  const approvers = (card.approved_by || [])
    .map((approver) => approver.username)
    .filter(Boolean);
  return (
    <div className="ch-cr-footer">
      <IconInfoCircle size={15} stroke={1.5} />
      <span>
        Via Change Request
        {approvers.length > 0 && (
          <>
            {" "}· Approved by <strong>{approvers.join(", ")}</strong>
          </>
        )}
      </span>
    </div>
  );
};

const Value = ({ value, className }) => (
  <td className={className}>
    {value === null || value === undefined || value === "" ? (
      <span className="ch-empty-value">{EMPTY}</span>
    ) : (
      value
    )}
  </td>
);

const ChangeTable = ({ rows }) =>
  rows.length > 0 ? (
    <table className="ch-table">
      <thead>
        <tr>
          <th className="ch-param">Parameter</th>
          <th className="ch-before">Before</th>
          <th className="ch-after">After</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={`${row.field}-${index}`}>
            <td className="ch-param">{row.label}</td>
            <Value value={row.before} className="ch-before" />
            <Value value={row.after} className="ch-after" />
          </tr>
        ))}
      </tbody>
    </table>
  ) : null;

/** Created / Modified / Deleted card with the Parameter / Before / After table. */
export const ChangeCard = ({ card, showSection }) => {
  const badge = ACTION_BADGE[card.action] || ACTION_BADGE.MODIFIED;
  const rows = Array.isArray(card.changes) ? card.changes : [];
  /* Product lines saved together arrive as one card with an entry per line. */
  const entries = Array.isArray(card.entries) ? card.entries : null;

  return (
    <div className="ch-card">
      <CardHeader card={card} showSection={showSection}>
        {card.entity_label && (
          <span className="ch-entity" title={card.entity_label}>
            {card.entity_label}
          </span>
        )}
        {KIND_BADGE[card.section] && (
          <span className={`ch-badge ${KIND_BADGE[card.section].className}`}>
            {KIND_BADGE[card.section].label}
          </span>
        )}
        {entries && (
          <span className="ch-count">
            {card.product_count}{" "}
            {(GROUP_NOUN[card.section] || ["change", "changes"])[card.product_count === 1 ? 0 : 1]}
          </span>
        )}
        {card.change_request_id && (
          <span className="ch-badge ch-badge-cr">Change Request</span>
        )}
        {card.actor_type === "AUTOMATION" && (
          <span className="ch-badge ch-badge-automation">Automation</span>
        )}
        {!entries && <span className={`ch-badge ${badge.className}`}>{badge.label}</span>}
      </CardHeader>

      {entries
        ? entries.map((entry, index) => {
            const entryBadge = ACTION_BADGE[entry.action] || ACTION_BADGE.MODIFIED;
            return (
              <div className="ch-entry" key={`${entry.entity_label}-${index}`}>
                <div className="ch-entry-head">
                  <span className={`ch-entry-title ch-dot-${entry.action?.toLowerCase()}`}>
                    {entry.entity_label}
                  </span>
                  <span className={`ch-badge ${entryBadge.className}`}>{entryBadge.label}</span>
                </div>
                <ChangeTable rows={Array.isArray(entry.changes) ? entry.changes : []} />
              </div>
            );
          })
        : <ChangeTable rows={rows} />}

      <ChangeRequestFooter card={card} />
    </div>
  );
};

/** Stage / sub-status move: "Pending → In Progress". */
export const StatusChangeCard = ({ card, showSection }) => (
  <div className="ch-card">
    <CardHeader card={card} showSection={showSection} plain>
      {card.status_track && card.status_track !== "stage" && (
        <span className="ch-track">{TRACK_LABEL[card.status_track] || card.status_track}</span>
      )}
      {card.change_request_id && (
        <span className="ch-badge ch-badge-cr">Change Request</span>
      )}
      {card.actor_type === "AUTOMATION" && (
        <span className="ch-badge ch-badge-automation">Automation</span>
      )}
    </CardHeader>
    <div className="ch-status-body">
      <span className="ch-status-pill">{card.status_from || EMPTY}</span>
      <IconArrowRight size={16} className="ch-status-arrow" />
      <span className="ch-status-pill ch-status-pill-to">{card.status_to || EMPTY}</span>
    </div>
    <ChangeRequestFooter card={card} />
  </div>
);

export const HistoryCard = ({ card, showSection }) =>
  card.action === "STATUS_CHANGED" ? (
    <StatusChangeCard card={card} showSection={showSection} />
  ) : (
    <ChangeCard card={card} showSection={showSection} />
  );
